<template>
  <b-form ref="form" @submit.stop.prevent="onSubmit">
    <!-- Ingredient Name -->
    <transition name="slide">
      <b-form-group label-cols-md="2" id="input-group-name" label="Name:" label-for="name">
        <vue-suggest
          mode="input"
          placeholder="Ingredient Name"
          :list="waterContents"
          :max-suggestions="10"
          :min-length="2"
          :styles="autoCompleteStyle"
          :filter-by-query="true"
          @select="onSuggestSelect"
          @hover="onSuggestHover"
          ref="suggestComponent"
          display-attribute="ingredient"
          value-attribute="ingredient"
        >
          <input class="form-control" v-model="name" />

          <div slot="suggestion-item" slot-scope="scope" :title="scope.suggestion.ingredient">
            <div class="text">
              <span v-html="boldenSuggestion(scope)"></span>
            </div>
          </div>

        </vue-suggest>
        <b-form-radio-group
          id="types"
          :options="typeOptions"
          v-model="ingredientType"
          @change="onIngredientTypeChange"
        />
      </b-form-group>
    </transition>

    <hr v-if="showAmount" />

    <!-- Ingredient Amount -->
    <transition name="slide">
      <b-form-group
        label-cols-md="2"
        v-if="showAmount"
        label="Amount:"
        label-for="amount"
        :description="amountValidator"
      >
        <b-input-group id="input-group-amount">
          <b-form-input
            @change="onAmountChange"
            id="amount"
            min="0"
            placeholder="Ingredient Amount"
            required
            type="number"
            step="any"
            v-model="amount"
          />

          <template v-if="amountType" v-slot:append>
            <b-form-select
              class="input-group-text"
              v-if="amountType === 'weight'"
              v-model="weightType"
              :options="weightOptions"
            />
            <div class="input-group-text" v-else>%</div>
          </template>
        </b-input-group>

        <b-form-radio-group id="amount-types" class="my-2" v-model="amountType">
          <b-form-radio value="weight">Weight</b-form-radio>
          <b-form-radio value="percent" :disabled="isFlour">Percent</b-form-radio>
        </b-form-radio-group>
      </b-form-group>
    </transition>

    <transition name="fade">
      <hr v-show="showHydration && isWet" />
    </transition>

    <!-- Water Amount-->
    <transition name="slide">
      <b-form-group
        label-cols-md="2"
        v-if="showHydration && isWet"
        id="input-group-water"
        label="Water:"
        label-for="hydration"
        :description="USDADisclaimer"
      >
        <b-input-group append="%">
          <b-form-input
            type="number"
            min="0"
            step="any"
            id="hydration"
            placeholder="Hydration"
            v-model="hydration"
          />
        </b-input-group>
      </b-form-group>
    </transition>
    <hr />
    <b-button :disabled="!validForm" type="submit" class="mx-auto">Add Ingredient</b-button>
  </b-form>
</template>

<script>
import { mapActions } from 'vuex'
import { checkFlour } from '@/utils/helpers'
import { WeightOptions } from '@/utils/constants'
import waterContent from '../../../data/waterContent.json'
import VueSuggest from 'vue-simple-suggest'

export default {
  name: 'NewIngredientForm',
  components: {
    VueSuggest
  },
  data() {
    return {
      name: '',
      ingredientType: null,
      amount: null,
      amountType: 'weight',
      weightType: WeightOptions.GRAMS,
      showAmount: false,
      showIngredientType: false,
      showHydration: false,
      USDADisclaimer: '',
      amountValidator: '',
      typeOptions: ['Wet', 'Dry'],
      weightOptions: [WeightOptions.GRAMS, WeightOptions.OUNCES],
      hydration: null,
      autoCompleteStyle: {
        vueSimpleSuggest: 'position-relative',
        inputWrapper: 'no-append mb-2',
        suggestions: 'position-absolute no-append list-group z-1000',
        suggestItem: 'list-group-item'
      }
    }
  },
  computed: {
    isWet() {
      return this.ingredientType === 'Wet'
    },
    validForm() {
      if (this.isWet) {
        return this.hydration !== null && this.showHydration && this.amount > 0 && this.name != ''
      }

      return this.amount !== null && this.amount != '' && this.ingredientType !== null
    },
    isFlour() {
      return checkFlour(this.name)
    },
    waterContents() {
      return waterContent
    }
  },
  methods: {
    ...mapActions(['fetchWaterWeight']),
    onSubmit(e) {
      let newIngredient = {
        name: this.name,
        amount: {
          value: this.amount,
          type: this.amountType,
          measurement: this.weightType
        },
        hydration: this.hydration
      }
      this.$emit('addIngredient', newIngredient)
    },
    onSuggestSelect(suggestion) {
      this.name = suggestion.ingredient
      if (this.ingredientType !== null) {
        this.showAmount = true
      }
      this.hydration = suggestion.hydration
      this.USDADisclaimer = '* Data pulled from the USDA Nutritional Database.'
      if (this.isFlour) {
        this.amountType = 'weight'
      }
    },
    onIngredientTypeChange(type) {
      if (this.name !== '') {
        this.showAmount = true

        if (type === 'Dry') {
          this.USDADisclaimer = ''
          this.hydration = null
        }
      }
    },
    onAmountChange(amount) {
      if (amount == 0) {
        this.amountValidator = '- Do you not want to add any of this ingredient?'
      } else if (amount < 0) {
        this.amountValidator = '- Does your recipe defy the laws of physics?'
      } else {
        this.amountValidator = ''
        this.showHydration = true
      }
    },
    onSuggestHover(name) {
      this.name = name.ingredient
    },
    boldenSuggestion(scope) {
      if (!scope) {
        return scope
      }
      const { suggestion, query } = scope
      let result = this.$refs.suggestComponent.displayProperty(suggestion)

      if (!query) {
        return result.ingredient
      }

      const texts = query.split(/[\s-_/\\|\.]/gm).filter(t => !!t) || ['']

      return result.replace(
        new RegExp('(.*?)(' + texts.join('|') + ')(.*?)', 'gi'),
        '$1<b>$2</b>$3'
      )
    }
  }
}
</script>

<style scoped lang="scss">
.btn {
  display: block;
  // color: #353c42;
  background-color: #0368a0;
  border-color: #01273c;

  &:disabled {
    background-color: darken(desaturate(#0368a0, 20%), 10%);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active {
  animation: slide-in 0.3s;
}
.slide-leave-active {
  animation: slide-in 0.3s reverse;
}
@keyframes slide-in {
  0% {
    margin-top: -4.8rem;
    opacity: 0;
  }
  50% {
    opacity: 0.05;
  }
  100% {
    margin-top: 0;
  }
}
</style>
